import { addDoc, collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import { FSCollections } from '@providers/firestoreProvider';
import { DemmiFS, Logger } from '@subhanhabib/demmilib';

import { db } from '../../../../firebase';
import { activateInviteCode, getInviteCode } from '../_inviteCodes';
import { FSCollectionNames } from '../networkService.helper';

export class FSCustomerAuth {
  static _getDocByAuth = async (authUID: string, provider: string): Promise<DemmiFS.User | undefined> => {
    const q = query(FSCollections.Users, where(`fb_authProviders.${provider}.uid`, '==', authUID));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return;
    if (querySnapshot.size !== 1) return;
    return {
      docID: querySnapshot.docs[0].id,
      ...(querySnapshot.docs[0].data() as DemmiFS.FSUser),
    };
  };

  static _getDocByID = async (customerDocID: string): Promise<DemmiFS.User | undefined> => {
    const docSnap = await getDoc(doc(db, FSCollectionNames.USERS, customerDocID));
    if (docSnap.exists()) {
      return {
        docID: docSnap.id,
        ...(docSnap.data() as DemmiFS.FSUser),
      };
    } else Logger({ messages: 'No such document!' }, this._getDocByID);
    return undefined;
  };

  static _getDocID = async (
    authUID: string,
    provider: DemmiFS.AuthProviderHelper.FBAuthProvider,
  ): Promise<string | undefined> => {
    Logger({ objs: { authUID, provider } }, this._getDocID);
    return this._getDocByAuth(authUID, provider).then(doc => doc?.docID);
  };

  static _getByProvider = async (
    authUID: string,
    provider: DemmiFS.AuthProviderHelper.FBAuthProvider,
  ): Promise<DemmiFS.User | undefined> => {
    return provider === DemmiFS.AuthProviderHelper.FBAuthProvider.apple
      ? this._getByProviderApple(authUID)
      : provider === DemmiFS.AuthProviderHelper.FBAuthProvider.google
        ? this._getByProviderGoogle(authUID)
        : undefined;
  };

  static _getByProviderApple = async (authUID: string): Promise<DemmiFS.User | undefined> => {
    return this._getDocByAuth(authUID, 'apple');
  };

  static _getByProviderGoogle = async (authUID: string): Promise<DemmiFS.User | undefined> => {
    return this._getDocByAuth(authUID, 'google');
  };

  static _createNewDoc = async (
    customerID: string,
    provider: DemmiFS.AuthProviderHelper.FBAuthProvider,
    authProviderInfo: DemmiFS.AuthProviderHelper.FBAuthProviderInfo,
    email: string,
    inviteCode: string,
  ): Promise<boolean> => {
    Logger({ objs: { customerID, authProviderInfo, inviteCode } }, this._createNewDoc);

    // Double check if email and code match
    const code = await getInviteCode(inviteCode);
    if (code?.email !== email) throw 'Invite code and email do not match!';

    const codeActivated = await activateInviteCode(inviteCode, email);
    if (!codeActivated) throw 'Failed to activate your invite code!';

    await addDoc(collection(db, FSCollectionNames.USERS), {
      inviteCode: inviteCode,
      // email: email,
      fb_authProviders: { [provider]: authProviderInfo },
    });

    return true;
  };
}
